import type { ReportChart, ReportImageRef } from './types';
import type { AttributeContext } from '../../config/attributes';
import { resolveTokens } from '../../config/attributes';

/**
 * The report's per-delivery in-text substitution.
 *
 * A report is authored once per campaign but delivered to many customers, so the
 * authored text carries `[Bracketed]` placeholders that resolve to THIS delivery's
 * values at render time. This module is the single home for the two things every
 * renderer must agree on: the placeholder syntax (the regex) and which tokens
 * resolve. The on-screen editor (`modules/report/assemble.ts`), the PDF and the
 * Word export (`modules/report/docx.ts`) all go through `applyTokens` so a bracket
 * resolves identically everywhere — and an unmatched bracket is left exactly as
 * authored rather than guessed at, unchanged from the original behaviour.
 */

/**
 * Replace every `[Token]` in `text` with `tokens[Token]`, leaving an unmatched
 * bracket verbatim. `transform` post-processes each resolved value (the Word path
 * passes its XML escaper), and is the identity for screen/PDF.
 *
 * When an {@link AttributeContext} is supplied, the shared `{{group.key}}` resolver runs FIRST as an
 * additive pass (see `config/attributes`), then the legacy `[Bracket]` pass runs over the result. The
 * two syntaxes are disjoint, so this never disturbs a legacy bracket; a `{{…}}` with no data is left
 * verbatim (no blank leak). With no `ctx` the behaviour is byte-identical to before.
 */
export function applyTokens(
  text: string,
  tokens: Record<string, string>,
  transform: (value: string) => string = (value) => value,
  ctx?: AttributeContext,
): string {
  const pre = ctx ? resolveTokens(text, ctx) : text;
  return pre.replace(/\[([^[\]]+)\]/g, (whole, key: string) =>
    Object.prototype.hasOwnProperty.call(tokens, key) ? transform(tokens[key]) : whole,
  );
}

function today(): string {
  return new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** The runtime values an author-facing per-delivery token resolves against. */
export interface DeliveryTokenContext {
  customerName: string;
  opportunityName?: string;
  seName?: string;
  accountId?: string;
  date?: string;
}

interface AuthorTokenDef {
  /** The bare token key, without brackets (e.g. `Customer Name`). */
  key: string;
  label: string;
  help: string;
  /**
   * Which resolver owns this token. `core` tokens are always available at assembly (`coreReportTokens`);
   * `delivery` tokens are registered by `templateTokens`. The split is load-bearing: `coreReportTokens`
   * must NOT emit the delivery tokens, or an authored `[Opportunity Name]` on a path that never sets one
   * (e.g. the AI figure path) would flip from surviving verbatim to resolving to an empty string.
   */
  owner: 'core' | 'delivery';
  resolve: (ctx: DeliveryTokenContext) => string;
  /** Legacy keys that resolve to the same value — kept working, but not advertised in the legend. */
  aliases?: string[];
}

/**
 * The single source of truth for the author-facing per-delivery tokens: their advertised legend AND how
 * each resolves. Add a token here once and both the Report Builder legend (`REPORT_TOKENS`, derived
 * below) and the resolved token maps (`coreReportTokens` / `resolveDeliveryTokens`) pick it up, so the
 * legend can no longer promise a token the runner doesn't fill (or vice versa). Template-prose brackets
 * that are filled automatically from captured data (never typed by an author) intentionally stay in
 * `templateTokens` and are not advertised here.
 */
const AUTHOR_TOKEN_DEFS: ReadonlyArray<AuthorTokenDef> = [
  {
    key: 'Customer Name',
    label: 'Customer name',
    help: "This delivery's account name.",
    owner: 'core',
    resolve: (c) => c.customerName,
    aliases: ['Customer Company Name'],
  },
  {
    key: 'Opportunity Name',
    label: 'Opportunity',
    help: 'The linked Salesforce opportunity name.',
    owner: 'delivery',
    resolve: (c) => c.opportunityName ?? '',
  },
  {
    key: 'SE Name',
    label: 'SE name',
    help: 'The SE running this delivery.',
    owner: 'delivery',
    resolve: (c) => c.seName ?? '',
  },
  {
    key: 'Account ID',
    label: 'Account ID',
    help: "The account's CCO / Salesforce id.",
    owner: 'delivery',
    resolve: (c) => c.accountId ?? '',
  },
  {
    key: 'Date',
    label: 'Date',
    help: "Today's date when the report is generated.",
    owner: 'core',
    resolve: (c) => c.date ?? today(),
  },
];

/** Resolve the catalog tokens (optionally just one owner's subset), including legacy aliases. */
function resolveAuthorTokens(
  ctx: DeliveryTokenContext,
  owner?: AuthorTokenDef['owner'],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const def of AUTHOR_TOKEN_DEFS) {
    if (owner && def.owner !== owner) continue;
    const value = def.resolve(ctx);
    out[def.key] = value;
    for (const alias of def.aliases ?? []) out[alias] = value;
  }
  return out;
}

/**
 * The always-available tokens, merged with an adapter's `extraTokens` (captured-data fills + the
 * delivery tokens `templateTokens` registers). Emits only the `core`-owned catalog tokens (the
 * `Customer Name` / legacy `Customer Company Name` aliases and `Date`); delivery tokens arrive via
 * `extra`. Behaviour is unchanged from the previous hand-written map.
 */
export function coreReportTokens(
  customerName: string,
  extra: Record<string, string> = {},
  date?: string,
): Record<string, string> {
  return { ...resolveAuthorTokens({ customerName, date }, 'core'), ...extra };
}

/**
 * The `delivery`-owned catalog tokens (`Opportunity Name`, `SE Name`, `Account ID`) for one delivery.
 * `templateTokens` composes this with its template-prose brackets so the legend and the resolver share
 * one definition of these author-facing tokens.
 */
export function resolveDeliveryTokens(ctx: DeliveryTokenContext): Record<string, string> {
  return resolveAuthorTokens(ctx, 'delivery');
}

/**
 * The per-delivery tokens an admin can type into any authored report text, surfaced as a legend by the
 * Report Builder editors. DERIVED from `AUTHOR_TOKEN_DEFS` so it can never drift from what resolves.
 */
export const REPORT_TOKENS: ReadonlyArray<{ token: string; label: string; help: string }> =
  AUTHOR_TOKEN_DEFS.map((def) => ({ token: `[${def.key}]`, label: def.label, help: def.help }));

function substituteChart(
  chart: ReportChart,
  tokens: Record<string, string>,
  ctx?: AttributeContext,
): ReportChart {
  return {
    ...chart,
    title: applyTokens(chart.title, tokens, undefined, ctx),
    source: applyTokens(chart.source, tokens, undefined, ctx),
    explanation: applyTokens(chart.explanation, tokens, undefined, ctx),
    unit: applyTokens(chart.unit, tokens, undefined, ctx),
    bars: chart.bars.map((bar) => ({ ...bar, label: applyTokens(bar.label, tokens, undefined, ctx) })),
    legend: chart.legend?.map((entry) => ({
      ...entry,
      label: applyTokens(entry.label, tokens, undefined, ctx),
    })),
  };
}

function substituteImage(
  image: ReportImageRef,
  tokens: Record<string, string>,
  ctx?: AttributeContext,
): ReportImageRef {
  return {
    ...image,
    title: applyTokens(image.title, tokens, undefined, ctx),
    caption: applyTokens(image.caption, tokens, undefined, ctx),
  };
}

/**
 * Resolve tokens in every authored string of a delivery's admin-authored figure set
 * (chart titles / sources / explanations / units / bar + legend labels, and image
 * titles / captions). The numeric data is untouched — only the authored text.
 */
export function substituteFigureText<T extends { charts: ReportChart[]; images: ReportImageRef[] }>(
  figures: T,
  tokens: Record<string, string>,
  ctx?: AttributeContext,
): T {
  return {
    ...figures,
    charts: figures.charts.map((chart) => substituteChart(chart, tokens, ctx)),
    images: figures.images.map((image) => substituteImage(image, tokens, ctx)),
  };
}
